import * as pmath from './pmath.js';
import { GLIDE, stepFlightVelocity, flightLaunchRetention, FLIGHT_LAUNCH_TIME as LAUNCH_TIME, FLIGHT_LAUNCH_TOTAL as LAUNCH_TOTAL,
  FLIGHT_LAUNCH_BRAKE as LAUNCH_BRAKE, FLIGHT_LAUNCH_THRUST as LAUNCH_THRUST,
  DIVE_PREPARATION, divePreparationRetention, divePreparationTurn, diveFallSpeed, FlightLaunchRecovery, HOVER, hoverVerticalVelocity } from './flightPhysics.js';
import type { RaceSectionKind } from './raceCourse.js';

/** Leaves time for the catch phase before each signed active-run gate. */
export const RACE_WALL_CHAIN_STRIDE = 16;

/** A reference input schedule, not a proof about every possible player input. */
export interface RaceTraversalPlan {
  pitch: number;
  coastDistance: number;
  brakeDistance: number;
  doubleJumpAt: number;
  estimatedFuel: number;
  fuelUpperBound: number;
  estimatedSeconds: number;
  landingError: number;
  /** Start X preparation / finish the dive at these forward takeoff distances. */
  diveStartDistance?: number;
  diveUntilDistance?: number;
  /** Release forward input here, then plunge vertically until this depth. */
  diveAlignDistance?: number;
  diveReleaseDrop?: number;
}

export interface RaceTrajectoryCandidate {
  kind: RaceSectionKind;
  /** Takeoff edge to target centre, in the candidate's horizontal travel frame. */
  distance: number;
  drop: number;
  landingRadius: number;
  wallCount: number;
  pitch: number;
  coastDistance: number;
  brakeDistance: number;
  doubleJumpAt: number;
  /** Explicit last-wall departure accommodates staggered longitudinal spacing. */
  wallDepartureDistance?: number;
  diveStartDistance?: number;
  diveUntilDistance?: number;
  diveAlignDistance?: number;
  diveReleaseDrop?: number;
}

export interface RaceTrajectoryResult {
  feasible: boolean;
  reason: 'landed' | 'short' | 'overshoot' | 'timeout';
  plan: RaceTraversalPlan;
  /** Local forward distance and height above the launch deck at coarse intervals. */
  samples: [number, number][];
  /** Launch-relative time matching each sampled position. */
  sampleTimes: number[];
}

/** Find the steepest approach that still settles over the pad. Slower funded
 * hover makes a random upward pitch both wasteful and a poor landing predictor.
 * The bracket is bounded and uses the same flight kernel as the actual route. */
export function planRaceApproach(candidate:RaceTrajectoryCandidate):RaceTrajectoryResult {
  if(candidate.kind==='jump')return simulateRaceTrajectory(candidate);
  // Arrive over the centre with one metre to spare before settling. This
  // protects the capsule against input timing and the world's radial frame.
  const approach={...candidate,drop:candidate.drop-1};
  let low=-.3,high=candidate.drop<0?0:.35;
  let best=simulateRaceTrajectory(approach);
  for(let i=0;i<11;i++){
    const pitch=(low+high)/2,result=simulateRaceTrajectory({...approach,pitch});
    // Samples may precede the last simulation step by up to 0.1 seconds;
    // landingError is the exact final distance, so use reason for a shortfall.
    if(result.reason==='short')high=pitch;else low=pitch;
    if(result.feasible&&(!best.feasible||result.plan.estimatedFuel<best.plan.estimatedFuel))best=result;
  }
  return best.feasible?simulateRaceTrajectory({...candidate,pitch:best.plan.pitch}):best;
}

const DT = 1 / 120;
const GRAVITY = 22.4;
const round = (value: number) => Math.round(value * 1000) / 1000;

/**
 * Pure scalar projection using the shipping flight kernel for gravity, lift,
 * momentum, trim, thrust and drag, plus deployment and hover controls. Walls use
 * a conservatively calibrated departure state; complete controller tests own
 * collision/catch validation. No client module is imported by shared code.
 */
export function simulateRaceTrajectory(candidate: RaceTrajectoryCandidate): RaceTrajectoryResult {
  const c = candidate;
  const wall = c.kind === 'wall' || c.kind === 'wall-chain';
  const launchOffset = c.kind === 'jump' ? 1.2 : .8;
  let x = -launchOffset, y = 0, vx = 7, vy = 7.5, time = 0, fuel = 0;
  let deployed = false, hover = false, braking = false, launchRemaining = 0, doubleJumped = false, stallTime = 0;
  let diveStarted = false, diveEnded = false, divePreparationRemaining = 0, diveStartAngle = 0;
  const launchRecovery = new FlightLaunchRecovery();
  const flight = { horizontal: vx, vertical: vy };
  if (wall) {
    x = c.wallDepartureDistance ?? (c.wallCount - 1) * RACE_WALL_CHAIN_STRIDE + 11.2;
    y = 1.35 + (c.wallCount - 1) * .15;
    const speed = 12.6;
    vx = speed * pmath.cos(c.pitch); vy = -speed * pmath.sin(c.pitch);
    time = c.wallCount * 1.05;
    deployed = true; launchRemaining = LAUNCH_TOTAL;
  }
  const samples: [number, number][] = [[round(x), round(y)]];
  const sampleTimes: number[] = [round(time)];
  let reason: RaceTrajectoryResult['reason'] = 'timeout';
  for (let tick = 0; tick < 1440; tick++) {
    const remaining = c.distance - x;
    const pastDive = c.diveReleaseDrop !== undefined ? y <= -c.diveReleaseDrop : x >= (c.diveUntilDistance ?? 18);
    const diving = c.kind === 'dive' && x >= (c.diveStartDistance ?? 2) && !pastDive;
    if (diving && !diveStarted) { diveStarted = true; divePreparationRemaining = DIVE_PREPARATION.time; diveStartAngle = pmath.atan2(vy, vx); }
    if (c.kind === 'dive' && !diveEnded && pastDive) { diveEnded = true; deployed = true; }
    if (!wall && c.kind !== 'dive' && !doubleJumped && time >= (c.kind === 'jump' ? c.doubleJumpAt : .25)) { vy = Math.max(vy, 6.5); doubleJumped = true; }
    if (c.kind !== 'jump' && c.kind !== 'dive' && !deployed && time >= .5) { deployed = true; launchRemaining = LAUNCH_TOTAL; }
    if (deployed && !braking && remaining < c.brakeDistance) { hover = true; braking = true; launchRemaining = 0; }
    if (!deployed || hover || diving) launchRecovery.reset();
    const previousY = y;
    let stepTime = DT;
    // A deliberate X press pitches the body over. Match the controller: the
    // smoothly braked speed swings from its X-press direction to straight down.
    if (divePreparationRemaining > 0) {
      while (stepTime > 1e-10 && divePreparationRemaining > 1e-10) {
        const brakeRemaining = divePreparationRemaining - (DIVE_PREPARATION.time - DIVE_PREPARATION.brake);
        const preparing = Math.min(stepTime, divePreparationRemaining, brakeRemaining > 1e-10 ? brakeRemaining : Infinity);
        const nextRemaining = Math.max(0, divePreparationRemaining - preparing);
        const speed = pmath.hypot(vx, vy) * divePreparationRetention(nextRemaining) / divePreparationRetention(divePreparationRemaining);
        const angle = diveStartAngle + (-pmath.TWO_PI / 4 - diveStartAngle) * divePreparationTurn(nextRemaining);
        vx = speed * pmath.cos(angle); vy = speed * pmath.sin(angle);
        x += vx * preparing; y += vy * preparing;
        divePreparationRemaining = nextRemaining; stepTime = Math.max(0, stepTime - preparing);
      }
      if (divePreparationRemaining <= 1e-10) {
        divePreparationRemaining = 0;
        vy = -pmath.hypot(vx, vy); vx = 0;
      }
    }
    // Match the controller's bounded windup steps: smooth braking, then coast
    // with the retained drift. Gravity, thrust and stalls wait for release.
    if (deployed && launchRemaining > LAUNCH_TIME) {
      while (stepTime > 1e-10 && launchRemaining > LAUNCH_TIME + 1e-10) {
        let windup = Math.min(stepTime, launchRemaining - LAUNCH_TIME);
        const brakeRemaining = launchRemaining - (LAUNCH_TOTAL - LAUNCH_BRAKE);
        if (brakeRemaining > 1e-10) windup = Math.min(windup, brakeRemaining);
        const nextRemaining = Math.max(LAUNCH_TIME, launchRemaining - windup);
        const ratio = flightLaunchRetention(nextRemaining) / flightLaunchRetention(launchRemaining);
        vx *= ratio; vy *= ratio;
        x += vx * windup; y += vy * windup;
        launchRemaining = nextRemaining; stepTime = Math.max(0, stepTime - windup);
      }
      if (launchRemaining <= LAUNCH_TIME + 1e-10) {
        launchRemaining = LAUNCH_TIME;
        const retainedSpeed = pmath.hypot(vx, vy);
        vx = retainedSpeed * pmath.cos(c.pitch); vy = -retainedSpeed * pmath.sin(c.pitch);
      }
    }
    if (stepTime <= 1e-10) {
      // Windup already integrated this tick's complete movement.
    } else if (diving) {
      const drift = c.diveAlignDistance !== undefined && x >= c.diveAlignDistance ? 0 : 14;
      vx += (drift - vx) * (1 - pmath.exp(-4 * stepTime));
      vy = -diveFallSpeed(-vy, GRAVITY, stepTime);
    } else if (hover) {
      vx += ((remaining < 1.1 ? 0 : 7.5) - vx) * (1 - pmath.exp(-7 * stepTime));
      vy = hoverVerticalVelocity(vy, GRAVITY, 1, stepTime);
      const paying = !braking && c.kind !== 'dive' && remaining >= c.coastDistance;
      if (!paying) fuel += GLIDE.boostBurn * HOVER.burnFraction * stepTime;
      if (paying) {
        const pitch = Math.min(c.pitch, .15);
        vx += GLIDE.thrust * pmath.cos(pitch) * stepTime;
        vy -= GLIDE.thrust * pmath.sin(pitch) * stepTime;
        fuel += GLIDE.boostBurn * stepTime;
        if (Math.sqrt(vx * vx + vy * vy) >= GLIDE.recoverSpeed) { hover = false; stallTime = 0; }
      }
    } else if (deployed) {
      const free = Math.min(stepTime, launchRemaining); launchRemaining = Math.max(0, launchRemaining - stepTime);
      if (launchRemaining < 1e-9) launchRemaining = 0;
      // Restore ordinary aerodynamics over the same launch-only tail as the
      // controller. Recovery adds neither engine power nor free fuel time.
      const recovery = launchRecovery.update(stepTime, free, free > 0 && launchRemaining === 0);
      const paying = c.kind !== 'dive' && remaining >= c.coastDistance;
      const power = paying ? 1 : free / stepTime;
      const thrustPower=power+(LAUNCH_THRUST-1)*free/stepTime;
      if (paying) fuel += GLIDE.boostBurn * (stepTime - free);
      flight.horizontal = vx; flight.vertical = vy;
      stepFlightVelocity(flight, { pitch: c.pitch, gravity: GRAVITY * (1 - free / stepTime), power, thrustPower, launchRecovery: recovery, yawRate: 0, brake: false, dt: stepTime });
      vx = flight.horizontal; vy = flight.vertical;
      stallTime = free === 0 && vx * vx + vy * vy < GLIDE.stallSpeed ** 2 ? stallTime + stepTime : 0;
      if (stallTime >= GLIDE.stallDelay) { hover = true; stallTime = 0; launchRecovery.reset(); }
    } else vy -= GRAVITY * stepTime;
    x += vx * stepTime; y += vy * stepTime; time += DT;
    if (tick % 12 === 0) { samples.push([round(x), round(y)]); sampleTimes.push(round(time)); }
    if (previousY >= -c.drop && y <= -c.drop && vy < 0) {
      const error = Math.abs(x - c.distance);
      reason = error <= c.landingRadius - .65 ? 'landed' : x < c.distance ? 'short' : 'overshoot';
      break;
    }
    if (y < -c.drop - 14) { reason = 'short'; break; }
  }
  const landingError = Math.abs(x - c.distance);
  return { feasible: reason === 'landed', reason, samples, sampleTimes, plan: {
    pitch: c.pitch, coastDistance: c.coastDistance, brakeDistance: c.brakeDistance, doubleJumpAt: c.doubleJumpAt,
    estimatedFuel: round(fuel), fuelUpperBound: round(fuel + (wall ? 3 : c.kind === 'jump' ? 0 : 2.5)),
    estimatedSeconds: round(time), landingError: round(landingError),
    ...(c.kind === 'dive' ? { diveStartDistance: c.diveStartDistance ?? 2, ...(c.diveReleaseDrop !== undefined ? {diveAlignDistance:c.diveAlignDistance,diveReleaseDrop:c.diveReleaseDrop} : {diveUntilDistance:c.diveUntilDistance??18}) } : {}),
  } };
}
